import React from 'react';
import { X, Package } from 'lucide-react';
import type { KitItem } from '../../types';

interface KitDetailModalProps {
  open: boolean;
  onClose: () => void;
  productSku: string;
  productTitle: string;
  items: KitItem[];
}

/**
 * Modal "Scheda kit" — mostra la lista dei componenti che compongono
 * un articolo "kit" (es. cartuccia rebuilt = vuoto + toner + chip).
 * Si apre cliccando il badge "Articoli del kit" nella riga catalogo.
 */
export function KitDetailModal({ open, onClose, productSku, productTitle, items }: KitDetailModalProps) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      data-testid="kit-detail-modal"
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-3 px-5 py-4 border-b bg-gradient-to-r from-amber-50 to-white">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Package className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider">Scheda kit</div>
            <div className="text-sm font-semibold tracking-tight truncate">{productTitle}</div>
            <div className="text-xs font-mono text-muted-foreground">Cod. DTK {productSku}</div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg border bg-white hover:bg-secondary/60 flex items-center justify-center transition-colors"
            data-testid="kit-detail-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          <div className="text-xs text-muted-foreground mb-3">
            Questo articolo è composto da <span className="font-semibold text-foreground">{items.length} prodotti</span>:
          </div>
          {items.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">Nessun dettaglio disponibile.</div>
          ) : (
            <ul className="divide-y border rounded-lg overflow-hidden">
              {items.map((it, idx) => (
                <li
                  key={`${it.sku}-${idx}`}
                  className="flex items-center gap-3 px-3 py-2.5 hover:bg-secondary/30 transition-colors"
                  data-testid={`kit-item-${it.sku}`}
                >
                  <div className="w-8 h-8 rounded-md bg-amber-50 border border-amber-100 flex items-center justify-center flex-shrink-0">
                    <Package className="w-3.5 h-3.5 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{it.label}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{it.sku}</div>
                  </div>
                  <div className="text-xs font-semibold tabular-nums">×{it.qty}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t bg-secondary/30 flex justify-end">
          <button
            onClick={onClose}
            className="h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium hover:bg-[hsl(214,58%,46%)] transition-colors"
            data-testid="kit-detail-close-footer"
          >
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}
